import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import { getProfile, type UserProfile } from "@/lib/profile-api";

/**
 * apps/web :: pages/cdl :: CdlPracticePage
 *
 * The `/cdl/practice` route — V0 stub for the CDL practice surface
 * (Wave 12 Pillar B). Paywall-gated upstream by `<PaidSurfaceGate
 * tier="cdl">` in App.tsx; this component is only mounted when the
 * caller has a CDL entitlement (or active trial).
 *
 * V0 scope:
 *   - "Coming soon" content — Wave 14 fills in the real practice UX.
 *   - Reads the user's `current_focus_state` so the title can name
 *     their state (matches the practice / dashboard pattern).
 */
type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; state: string }
  | { kind: "no-profile" };

export default function CdlPracticePage(): JSX.Element {
  const { t } = useTranslation();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile: UserProfile | null = await getProfile();
        if (cancelled) return;
        if (!profile) {
          setLoad({ kind: "no-profile" });
          return;
        }
        const state = profile.current_focus_state ?? profile.target_states[0] ?? "TX";
        setLoad({ kind: "ready", state });
      } catch {
        if (!cancelled) setLoad({ kind: "ready", state: "TX" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const state =
    load.kind === "ready" ? load.state : load.kind === "no-profile" ? "TX" : "TX";

  return (
    <LearnerPortalShell pageTitle={t("cdl.practice.title", { state })}>
      <article
        data-testid="cdl-practice-coming-soon"
        className="mx-auto mt-xl max-w-2xl px-md py-lg sm:px-lg"
      >
        <h1 className="font-display text-2xl font-bold text-brand-primary sm:text-3xl">
          {t("cdl.practice.comingSoon.heading")}
        </h1>
        <p className="mt-md text-neutral-700">
          {t("cdl.practice.comingSoon.body", { state })}
        </p>
      </article>
    </LearnerPortalShell>
  );
}
